import React from 'react'
import { TFunction } from 'i18next'
import { PageSize, withParsedContext } from '../../withJSSContext'
import { GenericTile } from '@christies/react/components/Tiles'
import { RelatedTileCarousel, GenericTileSkeleton } from '@christies/react/components/Magazine'
import { ResponsiveValue, Spacers, Theme } from '@christies/react/foundations'
import { track, TrackingEvents } from '@christies/react/utils'
import { useRelatedArticlesData } from './useRelatedArticlesData'
import { Endpoint } from '../../dataFetcher'

interface MagazinesRelatedArticlesModuleProps {
  t: TFunction
  parsedFields: {
    Heading: string
    endpoint: Endpoint
    story_url_format: string
    page_size: PageSize
  }
  parsedRouteFields: {}
  parsedSitecoreContext: {
    'Discovery.Feature.Magazines.StoriesByIdApiDefinition': Endpoint
  }
}

const MagazinesRelatedArticlesModule: React.FC<MagazinesRelatedArticlesModuleProps> = ({
  t,
  parsedFields,
  parsedSitecoreContext,
}) => {
  const storyAPIEndpoint = parsedSitecoreContext?.['Discovery.Feature.Magazines.StoriesByIdApiDefinition']
  const articlesEndpoint = parsedFields?.['endpoint']
  const urlFormat = parsedFields?.['story_url_format']

  const { data, status } = useRelatedArticlesData(
    {
      storyAPIEndpoint: storyAPIEndpoint,
      articlesAPIEndpoint: articlesEndpoint,
      storyUrlFormat: urlFormat,
      pageSize: parsedFields?.['page_size'],
    },
    import('./mock')
  )

  const carouselProps = {
    t: {
      previousButtonAriaLabel: t('feature-discovery-magazines-relatedstories-previousbuttonarialabel'),
      nextButtonAriaLabel: t('feature-discovery-magazines-relatedstories-nextbuttonarialabel'),
      slideDividerLabel: t('feature-discovery-magazines-relatedstories-rangesliderarialabel'),
    },
    theme: 'dark' as Theme,
    title: parsedFields?.['Heading'],
    tileWidth: {
      xs: 272,
      md: 360,
      xl: 264,
      xxl: 387,
    },
    gap: { xs: 'spacer5', md: 'spacer6', xl: 'spacer7' } as ResponsiveValue<Spacers>,
    componentStatus: 'initial' as const,
    loadingComponent: <GenericTileSkeleton />,
  }

  const getAnalytics = (e: React.MouseEvent, index: number): void => {
    track({
      event: TrackingEvents.INTERACTION,
      data: {
        interaction: {
          type: `article:${index}`,
          location: t('feature-discovery-magazines-relatedstories-analytics-location', {
            defaultValue: 'stories:related articles',
          }),
          destination: 'article',
        },
      },
    })
  }

  return (
    <RelatedTileCarousel {...carouselProps} componentStatus={status}>
      {(data || []).map((story, index: number) => (
        <GenericTile
          key={index.toString()}
          {...story}
          onClick={(e: React.MouseEvent): void => getAnalytics(e, index + 1)}
        />
      ))}
    </RelatedTileCarousel>
  )
}

export default withParsedContext(MagazinesRelatedArticlesModule as React.FC)
